import { useState } from 'react';
import { Link } from 'react-router';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatBRL, formatDateTime, formatPct } from '@/lib/format';
import { useApi } from '@/lib/useApi';

interface AlertItem {
  id: string;
  tipo: 'MARGEM_ALTA' | 'CUSTO_ZERO' | 'PREJUIZO' | 'CUSTO_ALTERADO';
  detalhe: Record<string, string> | null;
  createdAt: string;
  resolvido: boolean;
  resolvidoPor: string | null;
  resolvidoEm: string | null;
  venda: { id: string; numero: number | null; clienteNome: string; valorBruto: string; custoTotal: string | null; lucro: string | null; vendedorNome: string | null };
}

const TITULO: Record<AlertItem['tipo'], string> = {
  MARGEM_ALTA: 'Margem alta',
  CUSTO_ZERO: 'Custo zero',
  PREJUIZO: 'Venda com prejuízo',
  CUSTO_ALTERADO: 'Custo alterado',
};

function descricao(a: AlertItem): string {
  const d = a.detalhe ?? {};
  switch (a.tipo) {
    case 'MARGEM_ALTA':
      return `lucro de ${formatPct(d.margem ?? '0')} sobre ${formatBRL(a.venda.valorBruto)}`;
    case 'CUSTO_ZERO':
      return 'custo informado R$ 0,00';
    case 'PREJUIZO':
      return `custo ${formatBRL(d.custoTotal ?? a.venda.custoTotal ?? '0')} maior que a venda de ${formatBRL(a.venda.valorBruto)}`;
    case 'CUSTO_ALTERADO':
      return `de ${formatBRL(d.de ?? '0')} para ${formatBRL(d.para ?? '0')}${d.por ? ` por ${d.por}` : ''}`;
  }
}

/** Alertas de custo para o gestor revisar. */
export function AlertsPage() {
  const [todos, setTodos] = useState(false);
  const { data, error, reload } = useApi<AlertItem[]>(`/alerts${todos ? '?todos=1' : ''}`);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function resolve(id: string) {
    setBusy(id);
    setActionError(null);
    try {
      await api.post(`/alerts/${id}/resolve`);
      await reload();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Alertas de custo</h1>
          <p className="muted">Vendas que merecem uma olhada. Os alertas não bloqueiam nada; servem para revisão.</p>
        </div>
        <label className="check">
          <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} />
          Mostrar também os revisados
        </label>
      </header>
      {(error ?? actionError) && <Alert>{error ?? actionError}</Alert>}
      {!data ? (
        <p className="muted">Carregando…</p>
      ) : data.length === 0 ? (
        <section className="card">
          <p className="muted">Nenhum alerta para revisar.</p>
        </section>
      ) : (
        <ul className="alert-list">
          {data.map((a) => (
            <li key={a.id} className={`alert-item alert-item--${a.tipo.toLowerCase()} ${a.resolvido ? 'alert-item--done' : ''}`}>
              <div className="alert-item__body">
                <strong>{TITULO[a.tipo]}</strong>
                <p className="small">
                  Venda #{a.venda.numero ?? '—'} · {a.venda.clienteNome} · {a.venda.vendedorNome ?? 'sem vendedor'} · {descricao(a)}
                </p>
                <p className="muted small">
                  {formatDateTime(a.createdAt)}
                  {a.resolvido && <> · revisado por {a.resolvidoPor ?? '—'} em {formatDateTime(a.resolvidoEm)}</>}
                </p>
              </div>
              <div className="row">
                <Link className="btn btn--secondary btn--sm" to={`/vendas/${a.venda.id}`}>
                  Ver venda
                </Link>
                {!a.resolvido && (
                  <Button className="btn--sm" variant="ghost" loading={busy === a.id} onClick={() => void resolve(a.id)}>
                    Marcar como revisado
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
